import { Button } from "@mantine/core";
import { useEffect, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { ApiResponse } from "../../../api";
import { ModalFooter } from "../../../mantine/components/ModalFooter/ModalFooter";
import { useToast } from "../../../mantine/notifications/useToast";
import type { WizardFormModeProps } from "../../../types/wizard.types";
import type { Person } from "../../../utils/backend-types";
import { applyHighlightingToObjectFormFieldData } from "../../../utils/react-hook-form/applyHighlightingToObjectFormFieldData";
import { FormFieldController } from "../../BuildingBlocks/Forms/Controllers/FormFieldController";
import { setErrorsFromResponseData } from "../../BuildingBlocks/Forms/utils/setErrorsFromResponseData";
import type {
  GasConnectionCreateFormData,
  GasConnectionResponse
} from "../GasConnection.types";
import { GAS_CONNECTION_FORM_FIELD_DATA } from "./GasConnectionForm.constants";
import type { GasConnectionFormData } from "./GasConnectionForm.types";

type GasConnectionFormProps = {
  persons: Array<Person>;
  onCancel: () => void;
  onChangeNumErrors: (numErrors: number) => void;
  onChangeNumHighlightedFields: (numHighlightedFields: number) => void;
  onSubmitCreate: (
    data: GasConnectionCreateFormData
  ) => Promise<ApiResponse<GasConnectionResponse>>;
  onSubmitUpdate: (
    data: GasConnectionFormData,
    id: number
  ) => Promise<ApiResponse<GasConnectionResponse>>;
} & WizardFormModeProps<GasConnectionResponse>;

function GasConnectionForm({
  persons,
  onCancel,
  onChangeNumErrors,
  onChangeNumHighlightedFields,
  onSubmitCreate,
  onSubmitUpdate,
  ...modeProps
}: GasConnectionFormProps) {
  const { showErrorToast } = useToast();
  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setError,
    watch
  } = useForm<GasConnectionFormData>({
    defaultValues: modeProps.mode === "edit" ? modeProps.wizardObject : {}
  });
  const { formFieldData, numHighlightedFields } =
    applyHighlightingToObjectFormFieldData(
      GAS_CONNECTION_FORM_FIELD_DATA,
      watch,
      modeProps.mode === "edit" ? modeProps : undefined
    );
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const personChoices = persons.map((person) => ({
    value: person.id,
    label: person.name
  }));

  useEffect(() => {
    onChangeNumErrors(Object.keys(errors).length);
  }, [errors, onChangeNumErrors]);

  useEffect(() => {
    onChangeNumHighlightedFields(numHighlightedFields);
  }, [numHighlightedFields, onChangeNumHighlightedFields]);

  const onValidSubmit: SubmitHandler<GasConnectionFormData> = async (data) => {
    setIsSubmitting(true);

    try {
      return await (modeProps.mode === "create"
        ? onSubmitCreate(data)
        : onSubmitUpdate(data, modeProps.wizardObject.id));
    } catch (error) {
      setErrorsFromResponseData<GasConnectionFormData>(
        showErrorToast,
        error,
        watch(),
        setError,
        t("errors.UnknownError")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitCallback = handleSubmit(onValidSubmit);

  function handleClickCancel() {
    reset();
    onCancel();
  }

  return (
    <form className="GasConnectionForm" onSubmit={submitCallback}>
      <FormFieldController
        control={control}
        data={formFieldData.name}
        error={errors.name}
      />

      <FormFieldController
        control={control}
        data={formFieldData.pressure_stage}
        error={errors.pressure_stage}
      />

      <FormFieldController
        control={control}
        data={formFieldData.gas_quality}
        error={errors.gas_quality}
      />

      <FormFieldController
        control={control}
        data={{
          ...formFieldData.connecting_party,
          data: personChoices
        }}
        error={errors.connecting_party}
      />

      <ModalFooter>
        <Button
          disabled={isSubmitting}
          variant="default"
          onClick={handleClickCancel}
        >
          Abbrechen
        </Button>
        <Button
          color="secondary"
          loading={isSubmitting}
          onClick={submitCallback}
        >
          {modeProps.mode === "edit" ? "Speichern" : "Erstellen"}
        </Button>
      </ModalFooter>
    </form>
  );
}

export { GasConnectionForm, GasConnectionFormProps };
